import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PayPeriodCard({ period, onAddIncome, onDeleteIncome, newIncome, onIncomeChange, saving }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const statusColors = {
    positive: 'border-green-200 bg-green-50',
    negative: 'border-red-200 bg-red-50',
    neutral: 'border-gray-200 bg-gray-50'
  };

  const handleAdd = () => {
    if (newIncome.name && newIncome.amount) {
      onAddIncome(period.period_start_iso);
      setShowAddForm(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-5">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-purple-200 text-sm font-medium">Pay Period #{period.period}</p>
            <h3 className="text-xl font-bold mt-1">{period.date} — {period.end_date}</h3>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            period.leftover > 0 ? 'bg-green-500' : period.leftover < 0 ? 'bg-red-500' : 'bg-gray-500'
          }`}>
            ${period.leftover.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 divide-x divide-gray-200 border-b border-gray-200">
        <div className="p-4 text-center">
          <p className="text-xs text-gray-500 uppercase">Income</p>
          <p className="text-lg font-bold text-green-600">${period.income.toLocaleString()}</p>
        </div>
        <div className="p-4 text-center">
          <p className="text-xs text-gray-500 uppercase">Bills</p>
          <p className="text-lg font-bold text-red-600">${period.bills_total.toLocaleString()}</p>
        </div>
        <div className="p-4 text-center">
          <p className="text-xs text-gray-500 uppercase">Leftover</p>
          <p className={`text-lg font-bold ${period.leftover >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
            ${period.leftover.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Income Section */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-semibold text-gray-700">💵 Shared Income</h4>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="text-sm text-purple-600 hover:text-purple-800 font-medium"
          >
            + Add
          </button>
        </div>

        {showAddForm && (
          <div className="mb-3 p-3 bg-purple-50 rounded-lg">
            <div className="space-y-2">
              <input
                type="text"
                value={newIncome.name}
                onChange={(e) => onIncomeChange('name', e.target.value)}
                placeholder="Income source name"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
              <div className="flex gap-2">
                <input
                  type="number"
                  value={newIncome.amount}
                  onChange={(e) => onIncomeChange('amount', e.target.value)}
                  placeholder="Enter amount"
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <button
                  onClick={handleAdd}
                  disabled={saving || !newIncome.name || !newIncome.amount}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        {period.income_sources?.length > 0 ? (
          <div className="space-y-2">
            {period.income_sources.map((source) => (
              <div key={source.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{source.name}</p>
                  <p className="text-xs text-gray-500">Added by {source.contributor}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-green-600">${source.amount.toLocaleString()}</span>
                  <button
                    onClick={() => onDeleteIncome(source.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-2">No income added yet</p>
        )}
      </div>

      {/* Bills Section */}
      <div className="p-4">
        <h4 className="font-semibold text-gray-700 mb-3">📝 Bills Due</h4>
        {period.bills?.length > 0 ? (
          <div className="space-y-2">
            {period.bills.map((bill) => (
              <div key={bill.id} className="flex items-center justify-between py-2 px-3 bg-red-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{bill.name}</p>
                  <p className="text-xs text-gray-500">{bill.category} • Due day {bill.due_day}</p>
                </div>
                <span className="font-bold text-red-600">${bill.amount.toLocaleString()}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-2">No bills due this period</p>
        )}
      </div>
    </div>
  );
}

function HouseholdDashboard() {
  const { householdId } = useParams();
  const [household, setHousehold] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payDate, setPayDate] = useState(new Date());
  const [payFrequency, setPayFrequency] = useState('biweekly');
  const [forceThree, setForceThree] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [newIncome, setNewIncome] = useState({ name: '', amount: '' });

  const loadDashboard = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        pay_date: payDate.toISOString(),
        pay_frequency: payFrequency,
        force_three: forceThree.toString()
      });
      
      const response = await fetch(`${API}/households/${householdId}/dashboard?${params}`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setHousehold(data.household);
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [householdId, payDate, payFrequency, forceThree]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleAddIncome = async (periodStartIso) => {
    if (!newIncome.name || !newIncome.amount) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/households/${householdId}/income`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          pay_period_start: periodStartIso,
          name: newIncome.name,
          amount: parseFloat(newIncome.amount),
        }),
      });
      
      if (response.ok) {
        setNewIncome({ name: '', amount: '' });
        loadDashboard();
      }
    } catch (error) {
      console.error('Error adding income:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIncome = async (incomeId) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/households/${householdId}/income/${incomeId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        loadDashboard();
      }
    } catch (error) {
      console.error('Error deleting income:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSendSummary = async () => {
    if (!window.confirm('Send budget summary email to all household members?')) return;
    
    setSendingSummary(true);
    try {
      const response = await fetch(`${API}/households/${householdId}/send-summary`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Summary sent to ${data.members_notified} member(s)!`);
      } else {
        alert('Failed to send summary');
      }
    } catch (error) {
      console.error('Error sending summary:', error);
      alert('Failed to send summary');
    } finally {
      setSendingSummary(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading household budget...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-12">
        <div className="text-5xl mb-4">⚠️</div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Household not found</h2>
        <Link to="/household" className="text-purple-600 hover:text-purple-800">
          ← Back to Households
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <Link to="/household" className="text-gray-500 hover:text-gray-700 text-sm">
            ← Back to Households
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-2">
            👨‍👩‍👧 {household?.name}
          </h1>
          <p className="text-gray-600">{dashboardData.member_count} member(s) sharing this budget</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSendSummary}
            disabled={sendingSummary}
            className="px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium hover:bg-green-200 transition-colors disabled:opacity-50"
            data-testid="send-summary-btn"
          >
            {sendingSummary ? 'Sending...' : '📧 Email Summary'}
          </button>
          <Link
            to={`/household`}
            className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-medium hover:bg-purple-200 transition-colors"
          >
            Manage Members
          </Link>
        </div>
      </div>

      {/* Summary Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <p className="text-purple-200 text-sm">Total Income</p>
            <p className="text-3xl font-bold">${dashboardData.summary.total_income.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-purple-200 text-sm">Total Bills</p>
            <p className="text-3xl font-bold">${dashboardData.summary.total_bills.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-purple-200 text-sm">Total Leftover</p>
            <p className={`text-3xl font-bold ${dashboardData.summary.total_leftover >= 0 ? 'text-green-300' : 'text-red-300'}`}>
              ${dashboardData.summary.total_leftover.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-purple-200 text-sm">Pay Periods</p>
            <p className="text-3xl font-bold">{dashboardData.periods.length}</p>
          </div>
        </div>
      </div>

      {/* Settings */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <h2 className="font-bold text-gray-900 mb-4">⚙️ Pay Schedule</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Pay Frequency</label>
            <select
              value={payFrequency}
              onChange={(e) => setPayFrequency(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl"
            >
              <option value="biweekly">Every 2 weeks</option>
              <option value="weekly">Weekly</option>
              <option value="daily">Daily</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">First Pay Date</label>
            <input
              type="date"
              value={format(payDate, 'yyyy-MM-dd')}
              onChange={(e) => setPayDate(new Date(e.target.value + 'T12:00:00'))}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-xl w-full">
              <input
                type="checkbox"
                checked={forceThree}
                onChange={(e) => setForceThree(e.target.checked)}
                className="w-5 h-5 text-purple-600 rounded"
              />
              <span className="text-sm text-gray-700">Show extra pay period</span>
            </label>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <Link
          to={`/household/${householdId}/bills`}
          className="p-4 bg-blue-50 rounded-xl text-center hover:bg-blue-100 transition-colors border border-blue-200"
        >
          <div className="text-2xl mb-1">📝</div>
          <p className="font-medium text-blue-800">Manage Shared Bills</p>
          <p className="text-sm text-blue-600">{dashboardData.summary.total_bills > 0 ? `$${dashboardData.summary.total_bills} total` : 'Add bills'}</p>
        </Link>
        <Link
          to={`/household/${householdId}/income`}
          className="p-4 bg-green-50 rounded-xl text-center hover:bg-green-100 transition-colors border border-green-200"
        >
          <div className="text-2xl mb-1">💵</div>
          <p className="font-medium text-green-800">Manage Shared Income</p>
          <p className="text-sm text-green-600">{dashboardData.summary.total_income > 0 ? `$${dashboardData.summary.total_income} total` : 'Add income'}</p>
        </Link>
      </div>

      {/* Pay Periods */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">📅 Pay Periods</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {dashboardData.periods.map((period) => (
            <PayPeriodCard
              key={period.period}
              period={period}
              onAddIncome={handleAddIncome}
              onDeleteIncome={handleDeleteIncome}
              newIncome={newIncome}
              onIncomeChange={(field, value) => setNewIncome(prev => ({ ...prev, [field]: value }))}
              saving={saving}
            />
          ))}
        </div>
      </div>

      {/* Info */}
      <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
        <h3 className="font-bold text-gray-900 mb-2">💡 Tip</h3>
        <p className="text-gray-600 text-sm">
          All household members can add income and view the shared budget. 
          Use this to coordinate family finances and see everyone&apos;s contributions!
        </p>
      </div>
    </div>
  );
}

export default HouseholdDashboard;
